<script lang="ts">
// Glyphes Lucide (assets/ds/icons.ts) servis en data-URI de masque. Le SVG voyage dans le JS : ni
// font PrimeIcons (legacy), ni public/, exclu du build Docker.
//
// Le masque ne retient que l'ALPHA de la source : la couleur des tracés n'a aucun effet, la teinte
// vient de `background-color: currentColor` sur `.ds-icon`. Encodage mémoïsé, à la demande.
import { DS_ICON_SOURCES, type DsIconName } from '~/assets/ds/icons';

const ICON_MASKS = new Map<string, string>();

// `name` est accepté en `string` : le typage ne couvre pas les noms venus des données de navigation.
function maskOf (name: string): string | undefined {
  const cached = ICON_MASKS.get(name);
  if (cached !== undefined) { return cached; }

  const svg = (DS_ICON_SOURCES as Record<string, string>)[name];
  if (svg === undefined) { return undefined; }

  const mask = `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
  ICON_MASKS.set(name, mask);
  return mask;
}
</script>

<script lang="ts" setup>
// DsIcon — icône du Design System v2, masquée en currentColor (coloration via .ds-icon).
// `color` reprend la prop du composant amont (`<Icon color="var(--primary)" />`) : le masque étant
// peint en `currentColor`, il suffit de porter la couleur sur le span lui-même.
const props = withDefaults(defineProps<{ name: DsIconName; size?: number; color?: string }>(), { size: 16, color: undefined });

const mask = computed<string | undefined>(() => maskOf(props.name));

// Sans masque, le `background-color: currentColor` de `.ds-icon` peindrait un carré plein qui passe
// pour une icône : on n'affiche rien.
const style = computed(() => ({
  width: `${props.size}px`,
  height: `${props.size}px`,
  ...(props.color ? { color: props.color } : {}),
  ...(mask.value
    ? { WebkitMaskImage: mask.value, maskImage: mask.value }
    : { backgroundColor: 'transparent' }),
}));

// Les noms venus des données de navigation échappent au typage de la prop.
if (import.meta.dev) {
  watchEffect(() => {
    if (mask.value === undefined) {
      console.warn(`[DsIcon] nom d'icône inconnu : « ${props.name} » — aucun glyphe dans assets/ds/icons.ts`);
    }
  });
}
</script>

<template>
  <span
    class="ds-icon"
    aria-hidden="true"
    :style="style"
  />
</template>
